import { Module, Mutation, Action } from "vuex-class-modules"
import { apiService } from "@/services/api.service"
import { Endpoint } from "@/services/endpoints"
import { projectModule, ProjectActions } from "./project.module"
import { AlertActions, alertModule } from "./alert.module"
import { store } from "../store"
import { Modules } from "../modules"
import { BaseModule } from "@/models/BaseModule"
import { $t } from "@/plugins/i18n"
import { ServerResponse } from "@/models/ServerResponse"
import {
  ChangeMilestoneDate,
  getNextMilestoneTitle,
  Milestone,
  MilestoneAddEpic,
  MilestonePreview,
  MilestonePreviewResponse,
  MilestoneSimplify,
} from "@/models/Milestone"
import { BacklogSetup } from "@/models/Backlog"
import {
  MilestonePlanning,
  MilestonePlanningDTO,
  MilestonePlanningUpdate,
} from "@/models/MilestonePlanning"
import { AlertContentItem, AlertType } from "@/models/Alert"
import { formatDate, getTodayCalendarDate, nextMonths } from "@/utils/dateUtil"
import addDays from "date-fns/esm/addDays"
import { Filter, ProjectStats } from "@/models"
import { parsingServerResponseContent } from "@/utils/helpers"

interface GetMilestonePlanningPayload {
  epicId: string
  milestoneId: string
}

interface UpdateMilestonePlanningPayload {
  epicId: string
  body: MilestonePlanningUpdate
}

export enum MilestoneActions {
  GET_MILESTONE = "GET_MILESTONE",
  POST_MILESTONE = "POST_MILESTONE",
  POST_MILESTONE_EPIC = "POST_MILESTONE_EPIC",
  DELETE_MILESTONE_EPIC = "DELETE_MILESTONE_EPIC",
  ADD_NEW_MILESTONE = "ADD_NEW_MILESTONE",
  CANCEL_MILESTONE = "CANCEL_MILESTONE",
  CHANGE_MILESTONE_DATE = "CHANGE_MILESTONE_DATE",
  CHANGE_MILESTONE = "CHANGE_MILESTONE",
  GET_MILESTONES = "GET_MILESTONES",
  GET_MILESTONES_REVIEW = "GET_MILESTONES_REVIEW",
  GET_MILESTONES_ALL = "GET_MILESTONES_ALL",
  UPDATE_MILESTONE_IN_LIST = "UPDATE_MILESTONE_IN_LIST",
  GET_MILESTONES_ALL_CONTENT = "GET_MILESTONES_ALL_CONTENT",
  CREATE_NEW_MILESTONE = "CREATE_NEW_MILESTONE",
  SET_LAST_MILESTONE = "SET_LAST_MILESTONE",
  ADD_LAST_MILESTONE = "ADD_LAST_MILESTONE",
  GET_MILESTONES_PERIOD = "GET_MILESTONES_PERIOD",
  GET_LAST_MILESTONE = "GET_LAST_MILESTONE",
  LIST_OF_MILESTONE_CHARTS = "LIST_OF_MILESTONE_CHARTS",
  RESET_ALL_MILESTONES = "RESET_ALL_MILESTONES",
  DELETE_MILESTONE = "DELETE_MILESTONE",
  ALL_MILESTONES_ALL_TIME_METRICS = "ALL_MILESTONES_ALL_TIME_METRICS",
  GET_SIMPLIFY_MILESTONES = "GET_SIMPLIFY_MILESTONES",
  GET_MILESTONE_EXCLUDING_COMING_STATUS = "GET_MILESTONE_EXCLUDING_COMING_STATUS",
  GET_LAST_MILESTONE_IN_LIST = "GET_LAST_MILESTONE_IN_LIST",
  STOP_MILESTONE = "STOP_MILESTONE",
  GET_MILESTONE_PLANNING = "GET_MILESTONE_PLANNING",
  GET_MILESTONE_PLANNING_SNAPSHOT = "GET_MILESTONE_PLANNING_SNAPSHOT",
  UPDATE_MILESTONE_PLANNING = "UPDATE_MILESTONE_PLANNING",
}
@Module
class MilestoneModule extends BaseModule {
  milestone: Milestone
  milestones: ServerResponse<Milestone> = new ServerResponse<Milestone>()
  simplifyMilestones: ServerResponse<MilestoneSimplify> = new ServerResponse<MilestoneSimplify>()
  reviewedMilestones: ServerResponse<Milestone> = new ServerResponse<Milestone>()
  milestoneCharts: ServerResponse<MilestonePreview> = new ServerResponse<MilestonePreview>()
  allMilestones: ServerResponse<Milestone> = new ServerResponse<Milestone>()
  allMilestonesAllTimeMetrics: ProjectStats = new ProjectStats()

  get projectId() {
    return projectModule.currentProjectId
  }

  get [`get/${MilestoneActions.GET_MILESTONES}`]() {
    return this.milestones
  }

  get [`get/${MilestoneActions.GET_MILESTONES_REVIEW}`]() {
    return this.reviewedMilestones
  }

  get [`get/${MilestoneActions.GET_MILESTONE}`]() {
    return this.milestone
  }

  get [`get/${MilestoneActions.LIST_OF_MILESTONE_CHARTS}`]() {
    return this.milestoneCharts
  }

  get [`get/${MilestoneActions.ALL_MILESTONES_ALL_TIME_METRICS}`]() {
    return this.allMilestonesAllTimeMetrics
  }

  get [`get/${MilestoneActions.GET_LAST_MILESTONE_IN_LIST}`]() {
    const index = this.milestones.content.length - 1
    return this.milestones.content[index]
  }

  get [`get/${MilestoneActions.GET_MILESTONES_ALL}`]() {
    return this.allMilestones
  }

  get [`get/${MilestoneActions.GET_SIMPLIFY_MILESTONES}`]() {
    return this.simplifyMilestones
  }

  get [`get/${MilestoneActions.GET_MILESTONES_ALL_CONTENT}`]() {
    const reviewContent = this.reviewedMilestones?.content ?? []
    const milestoneContent = this.milestones?.content ?? []
    return [...reviewContent, ...milestoneContent]
  }

  @Mutation
  setMilestones(milestone: ServerResponse<Milestone>) {
    this.milestones = milestone
  }

  @Mutation
  setSimplifyMilestones(milestoneResponse: ServerResponse<MilestoneSimplify>) {
    const content = parsingServerResponseContent({
      originalContent: this.simplifyMilestones.content,
      serverResponseContent: milestoneResponse.content,
      numberOfPage: milestoneResponse.pageable.pageNumber,
    })
    this.simplifyMilestones = { ...milestoneResponse, content }
  }
  
  @Mutation
  setAllMilestone() {
    const content = [...this.reviewedMilestones.content, ...this.milestones.content]
    this.allMilestones = this.milestones
    this.allMilestones.content = content
  }

  @Mutation
  resetAllMilestones() {
    this.allMilestones = new ServerResponse<Milestone>()
  }

  @Mutation
  setMilestoneInList(milestone: Milestone) {
    const milestoneIndex = this.milestones.content.findIndex(e => e.id === milestone.id)
    if (milestoneIndex === -1) {
      this.milestones.content.push(milestone)
      return
    }
    this.milestones.content[milestoneIndex] = milestone
  }

  @Mutation
  setMilestoneReview(milestone: ServerResponse<Milestone>) {
    this.reviewedMilestones = milestone
  }

  @Mutation
  setMilestone(milestone: Milestone) {
    this.milestone = milestone
  }

  @Mutation
  setAllMetrics(allMilestonesAllTimeMetrics: ProjectStats) {
    this.allMilestonesAllTimeMetrics = allMilestonesAllTimeMetrics
  }

  @Mutation
  newMilestone() {
    const milestones = this.milestones.content
    const previousMilestone = milestones[milestones.length - 1] || this.reviewedMilestones.content[this.reviewedMilestones.content.length - 1]
    const newMilestoneTitle = previousMilestone?.title
      ? getNextMilestoneTitle(previousMilestone.title)
      : "A"
    const newStartDay = previousMilestone?.endDate ?? getTodayCalendarDate()
    const addNewMilestone = new Milestone({
      title: newMilestoneTitle,
      startDate: newStartDay,
      endDate: nextMonths(newStartDay, 3),
      epics: [],
      newMilestone: true,
      status: "COMING",
    })
    milestones.push(addNewMilestone)
  }

  @Mutation
  setLastMilestone(milestone: Milestone) {
    const index = this.allMilestones.content.length ? this.milestones.content.length - 1 : 0
    this.allMilestones.content[index] = milestone
  }

  @Mutation
  addLastMilestone(milestone: Milestone) {
    this.allMilestones.content.push(milestone)
  }

  @Mutation
  cancelMilestone() {
    const milestones = this.milestones.content
    milestones.splice(-1)
  }

  @Mutation
  setMilestoneCharts(milestoneCharts: ServerResponse<MilestonePreview>) {
    this.milestoneCharts = milestoneCharts
  }

  @Mutation
  updateMilestoneInList() {
    this.milestones.content = this.milestones.content.map((e) => {
      if (e.id === this.milestone.id) {
        e = this.milestone
      }
      return e
    })
  }

  @Mutation
  updateMilestone({ field, value, milestoneId } : {field: string, value: any, milestoneId: string}) {
    if (milestoneId.length) {
      this.milestones.content = this.milestones.content.map((e) => {
        if (e.id === milestoneId) e[field] = value
        return e
      })
    } else {
      const lastIndex = this.milestones.content.length - 1
      this.milestones.content[lastIndex][field] = value
    }
  }

  @Action
  async [MilestoneActions.GET_MILESTONE](milestoneId: string) {
    const data = await apiService.get<Milestone>(Endpoint.GET_MILESTONE(this.projectId, milestoneId))
    return this.handleResponse<Milestone>(data, (data) => {
      const milestone = new Milestone(data)
      this.setMilestone(milestone)

      return milestone
    })
  }

  @Action
  async [MilestoneActions.STOP_MILESTONE](milestoneId: string) {
    await apiService.patch<Milestone>(Endpoint.STOP_MILESTONE(this.projectId, milestoneId))
  }

  @Action
  async [MilestoneActions.GET_MILESTONE_PLANNING](epicId: string) {
    const endpoint = Endpoint.EPIC_MILESTONE_PLANNING(this.projectId, epicId)
    const data = await apiService.get<MilestonePlanningDTO>(endpoint)
    return this.handleResponse<MilestonePlanningDTO>(data, data => new MilestonePlanning(data))
  }

  // a closed milestone is an archive, so it needs the milestone explicitly
  @Action
  async [MilestoneActions.GET_MILESTONE_PLANNING_SNAPSHOT]({
    epicId,
    milestoneId,
  }: GetMilestonePlanningPayload) {
    const endpoint = Endpoint.GET_EPIC_MILESTONE_PLANNING(this.projectId, epicId, milestoneId)
    const data = await apiService.get<MilestonePlanningDTO>(endpoint)
    return this.handleResponse<MilestonePlanningDTO>(data, data => new MilestonePlanning(data))
  }

  @Action
  async [MilestoneActions.UPDATE_MILESTONE_PLANNING]({
    epicId,
    body,
  }: UpdateMilestonePlanningPayload) {
    const endpoint = Endpoint.EPIC_MILESTONE_PLANNING(this.projectId, epicId)
    const data = await apiService.patch<void>(endpoint, body)
    return this.handleResponse<void>(data)
  }

  @Action
  async [MilestoneActions.DELETE_MILESTONE](milestone: Milestone) {
    // TODO - Perhaps this endpoint will not be used to add new epic in milestone
    if(!milestone?.id) return
    const data = await apiService.delete<void>(Endpoint.GET_MILESTONE(this.projectId, milestone.id))
    return this.handleResponse<void>(data,() => {
      const alertContent: AlertContentItem[] = [
        { text: `${milestone.titleParsed}`, type: "bold" },
        { text: ` ${$t("alert.deleted")}`, type: "regular" },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
    })
  }

  @Action
  async [MilestoneActions.POST_MILESTONE_EPIC](payload: MilestoneAddEpic) {
    // TODO - Perhaps this endpoint will not be used to add new epic in milestone
    const data = await apiService.post<Milestone>(Endpoint.POST_MILESTONE_EPIC(this.projectId, payload.milestoneId, payload.epicId))
    return this.handleResponse<Milestone>(data)
  }

  @Action
  async [MilestoneActions.DELETE_MILESTONE_EPIC](payload: MilestoneAddEpic) {
    const data = await apiService.delete<Milestone>(Endpoint.POST_MILESTONE_EPIC(this.projectId, payload.milestoneId, payload.epicId))
    return this.handleResponse<Milestone>(data)
  }

  @Action
  async [MilestoneActions.POST_MILESTONE]({ milestone, projectId }: { milestone: Milestone, projectId?: string }) {
    const data = await apiService.post<Milestone>(Endpoint.POST_MILESTONE(projectId ?? this.projectId), milestone.getJsonObj())
    return this.handleResponse<Milestone>(data, (data) => {
      const alertContent: AlertContentItem[] = [{
        text: `Milestone ${$t("alert.created")}`,
        type: "regular",
      }]
      alertModule[AlertActions.SHOW_ALERT]({
        content: alertContent,
        type: AlertType.SUCCESS,
        theme: "toast",
      })
      const milestone = new Milestone(data)
      this.setMilestone(milestone)
      this.setLastMilestone(milestone)
      return milestone
    })
  }

  @Action
  async [MilestoneActions.GET_MILESTONES](backlogSetup: BacklogSetup) {
    const query = backlogSetup.filter.getJsonObj()
    const data = await apiService.get<ServerResponse<Milestone>>(Endpoint.GET_MILESTONES(this.projectId), query)
    return this.handleResponse<ServerResponse<Milestone>>(data, (data) => {
      const content: Milestone[] = data.content.map(e => new Milestone(e))
      this.setMilestones(Object.assign(data, { content }))
    })
  }

  @Action
  async [MilestoneActions.GET_LAST_MILESTONE]({ projectId }: { projectId?: string } = {}) {
    const data = await apiService.get<Milestone>(Endpoint.GET_LAST_MILESTONE(projectId ?? this.projectId))
    return this.handleResponse<Milestone>(data, (data) => {
      return new Milestone(data)
    })
  }

  @Action
  async [MilestoneActions.GET_MILESTONES_REVIEW](backlogSetup: BacklogSetup) {
    const query = backlogSetup.filter.getJsonObj()
    const data = await apiService.get<ServerResponse<Milestone>>(Endpoint.GET_MILESTONES_REVIEW(this.projectId), query)
    return this.handleResponse<ServerResponse<Milestone>>(data, (data) => {
      const content: Milestone[] = data.content.map((e) => {
        e.status = "REVIEW"
        return new Milestone(e)
      })
      this.setMilestoneReview(Object.assign(data, { content }))
    })
  }

  @Action
  async [MilestoneActions.GET_MILESTONES_PERIOD]({ body, projectId }: { body: ChangeMilestoneDate, projectId: string }) {
    const data = await apiService.get<ServerResponse<Milestone>>(Endpoint.MILESTONES_PERIOD(projectId), body.getJsonObj())
    return this.handleResponse<ServerResponse<Milestone>>(data, (data) => {
      const content: Milestone[] = data.content.map(e => new Milestone(e))
      this.setMilestones(Object.assign(data, { content }))
    })
  }

  @Action
  async [MilestoneActions.LIST_OF_MILESTONE_CHARTS]({ projectId, onlyGet, filter }: { projectId: string, onlyGet?: boolean, filter: Filter }) {
    const data = await apiService.get<MilestonePreviewResponse>(Endpoint.LIST_OF_MILESTONE_CHARTS(projectId), filter.getJsonObj())
    return this.handleResponse<MilestonePreviewResponse>(data, (data) => {
      if (!onlyGet) {
        this.setMilestoneCharts(data.milestones)
        this.setAllMetrics(data.allMilestonesAllTimeMetrics)
      }
      return data
    })
  }

  @Action
  async [MilestoneActions.GET_SIMPLIFY_MILESTONES]({ onlyGet, filter }: { onlyGet?: boolean, filter: Filter }) {
    const data = await apiService.get<ServerResponse<MilestoneSimplify>>(Endpoint.GET_SIMPLIFY_MILESTONES(this.projectId), filter.getJsonObj())
    return this.handleResponse<ServerResponse<MilestoneSimplify>>(data, (data) => {
      const content = data.content.map((e) => new MilestoneSimplify(e))
      data.content = content
      if (!onlyGet) {
        this.setSimplifyMilestones(data)
      }
      return data
    })
  }

  @Action
  async [MilestoneActions.GET_MILESTONE_EXCLUDING_COMING_STATUS]({ onlyGet, filter }: { onlyGet?: boolean, filter: Filter }) {
    const data = await apiService.get<ServerResponse<MilestoneSimplify>>(Endpoint.GET_MILESTONE_EXCLUDING_COMING_STATUS(this.projectId), filter.getJsonObj())
    return this.handleResponse<ServerResponse<MilestoneSimplify>>(data, (data) => {
      data.content = data.content.map((e) => new MilestoneSimplify(e))
      if (!onlyGet) {
        this.setSimplifyMilestones(data)
      }
      return data
    })
  }

  @Action
  async [MilestoneActions.GET_MILESTONES_ALL](backlogSetup: BacklogSetup) {
    const milestonesReview = this[MilestoneActions.GET_MILESTONES_REVIEW]
    const milestones = this[MilestoneActions.GET_MILESTONES]
    await Promise.all([
      milestonesReview(backlogSetup),
      milestones(backlogSetup),
    ])
    this.setAllMilestone()
  }

  @Action
  async [MilestoneActions.ADD_NEW_MILESTONE]() {
    this.newMilestone()
  }

  @Action
  async [MilestoneActions.CANCEL_MILESTONE]() {
    this.cancelMilestone()
  }

  @Action
  async [MilestoneActions.CHANGE_MILESTONE]({ field, value, milestoneId } : {field: string, value: any, milestoneId: string}) {
    this.updateMilestone({ field, value, milestoneId })
  }

  @Action
  async [MilestoneActions.CHANGE_MILESTONE_DATE]({ params, projectId }: { params: ChangeMilestoneDate, projectId?: string }) {
    const data = await apiService.patch<Milestone>(Endpoint.CHANGE_MILESTONE_DURATION(projectId ?? this.projectId, params.milestone?.id ?? ""), params.getJsonObj())
    return this.handleResponse<Milestone>(data, (data) => {
      const startDate = formatDate(params.startDate, "M/dd/yyyy", { dateOnly: true })
      const endDate = formatDate(params.endDate, "M/dd/yyyy", { dateOnly: true })
      const alertContent: AlertContentItem[] = [
        { text: `${params.milestone.titleParsed} `, type: "bold" },
        { text: "dates changed to ", type: "regular" },
        { text: `${startDate} `, type: "bold" },
        { text: "and ", type: "regular" },
        { text: `${endDate} `, type: "bold" },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
      const milestone = new Milestone(data)
      this.setMilestoneInList(milestone)
      return milestone
    })
  }

  @Action
  async [MilestoneActions.UPDATE_MILESTONE_IN_LIST]() {
    this.updateMilestoneInList()
  }

  @Action
  async [MilestoneActions.SET_LAST_MILESTONE](milestone) {
    this.setLastMilestone(milestone)
  }

  @Action
  async [MilestoneActions.ADD_LAST_MILESTONE](milestone) {
    this.addLastMilestone(milestone)
  }

  @Action
  async [MilestoneActions.RESET_ALL_MILESTONES]() {
    this.resetAllMilestones()
  }
}

export const milestoneModule = new MilestoneModule({
  store,
  name: Modules.MILESTONE,
})
